/**
 * Cache updates after an action on the connector (a block on the Teams page, a
 * tool switch on the Tools page), kept apart from the hooks so they can be
 * tested as plain functions.
 */

import type {
  ConnectorOverview,
  ConnectorTool,
  McpConnectorUser,
  McpUserBlockStatus,
  TeamsOverview,
} from "@/lib/api/generated";

/**
 * The connector overview with one tool as the server just returned it after a
 * switch, so its row (switch and state) changes at once rather than after a
 * refetch. A tool the overview does not list, or an overview that is not
 * loaded, is left alone: the refetch that follows brings it in.
 */
export function withTool(
  overview: ConnectorOverview | undefined,
  tool: ConnectorTool,
): ConnectorOverview | undefined {
  if (overview === undefined || !overview.tools.some((known) => known.name === tool.name)) {
    return overview;
  }
  return {
    ...overview,
    tools: overview.tools.map((known) => (known.name === tool.name ? tool : known)),
  };
}

/**
 * The connector users with one person's block as the server just confirmed it,
 * so their row changes the moment the dialog closes rather than after a
 * refetch. Like the server's list: someone blocked before any call is added,
 * and someone never seen drops out once unblocked.
 */
export function withBlockStatus(
  users: McpConnectorUser[] | undefined,
  status: McpUserBlockStatus,
): McpConnectorUser[] | undefined {
  if (users === undefined) {
    return users;
  }
  const block = {
    blocked: status.blocked,
    blocked_at: status.blocked ? (status.blocked_at ?? null) : null,
    blocked_by: status.blocked ? (status.blocked_by ?? null) : null,
    blocked_by_email: status.blocked ? (status.blocked_by_email ?? null) : null,
  };
  const known = users.find((user) => user.user_oid === status.user_oid);
  if (known === undefined) {
    if (!status.blocked) {
      return users;
    }
    return [
      ...users,
      {
        user_oid: status.user_oid,
        user_email: status.user_email ?? null,
        first_seen_at: null,
        last_seen_at: null,
        calls_last_7_days: 0,
        ...block,
      },
    ];
  }
  if (!status.blocked && !known.first_seen_at) {
    return users.filter((user) => user !== known);
  }
  return users.map((user) =>
    user === known
      ? { ...user, user_email: user.user_email ?? status.user_email ?? null, ...block }
      : user,
  );
}

/**
 * The Teams page's people with one person's block as the server just
 * confirmed it, matched by their Microsoft id, so their row changes the moment
 * the dialog closes rather than after a refetch. A page that is not loaded, or
 * that lists nobody with that id, is left alone: the refetch that follows
 * brings it in.
 */
export function withPersonBlock(
  overview: TeamsOverview | undefined,
  status: McpUserBlockStatus,
): TeamsOverview | undefined {
  if (overview === undefined || !overview.people.some((p) => p.user_oid === status.user_oid)) {
    return overview;
  }
  return {
    ...overview,
    people: overview.people.map((person) =>
      person.user_oid === status.user_oid
        ? {
            ...person,
            blocked: status.blocked,
            blocked_at: status.blocked ? (status.blocked_at ?? null) : null,
            blocked_by_email: status.blocked ? (status.blocked_by_email ?? null) : null,
          }
        : person,
    ),
  };
}
